import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-ab-testing";
const TITLE = "Shopify A/B-Testing: Tests, die Entscheidungen tragen";
const DESCRIPTION =
  "A/B-Testing für Shopify mit wenig Traffic: welche Tests sich lohnen, wie lange sie laufen müssen und welche Fehler Ergebnisse wertlos machen.";

const faqs = [
  {
    q: "Ab wie viel Traffic lohnt sich A/B-Testing?",
    a: "Als grobe Schwelle gelten rund 1.000 Bestellungen pro Monat. Darunter dauern aussagekräftige Tests so lange, dass Saison und Marktbewegung das Ergebnis verfälschen. Dann sind Best-Practice-Umbauten mit Vorher-Nachher-Messung der bessere Weg.",
  },
  {
    q: "Wie lange muss ein Test laufen?",
    a: "Mindestens zwei volle Wochen, immer über komplette Wochenzyklen und nie mitten in einer Kampagne oder einem Sale. Vorher abzubrechen, weil eine Variante führt, ist der häufigste Fehler überhaupt.",
  },
  {
    q: "Was, wenn ein Test kein Ergebnis bringt?",
    a: "Ein Test ohne Unterschied ist kein verlorener Test, sondern eine gesparte Fehlinvestition. Er verhindert, dass eine Idee dauerhaft ins Produkt wandert, die nichts bringt.",
  },
  {
    q: "Welches Tool empfehlen Sie für Shopify?",
    a: "Das Tool ist zweitrangig, solange es serverseitig oder ohne Flackern ausspielt und sich sauber mit Ihren Bestelldaten verbinden lässt. Wichtiger ist die Disziplin: eine Hypothese, eine Kennzahl, eine feste Laufzeit.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-ab-testing")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Testing"
      title="Shopify A/B-Testing: weniger Tests, bessere Entscheidungen"
      intro="Die meisten Shops testen zu viel Kleines und zu wenig Grundsätzliches. Ein guter Test beantwortet keine Geschmacksfrage, sondern eine geschäftliche, und sein Ergebnis hält länger als die nächste Saison."
      source="ratgeber-ab-testing"
      sections={[
        {
          h: "Zuerst die ehrliche Traffic-Frage",
          p: [
            "Statistische Aussagekraft braucht Menge. Wer 200 Bestellungen im Monat hat, kann keinen Unterschied von fünf Prozent nachweisen, und sollte auch nicht so tun.",
            "Rechnen Sie vor jedem Test die nötige Laufzeit aus: erwartete Verbesserung, aktuelle Conversion-Rate, Bestellungen pro Woche. Kommt eine Zahl über acht Wochen heraus, ist die Idee kein Testkandidat, sondern eine Entscheidung.",
          ],
        },
        {
          h: "Was sich zu testen lohnt",
          p: [
            "Testen Sie Dinge, die eine Annahme über Ihre Kunden prüfen, nicht Dinge, die eine Vorliebe bestätigen.",
          ],
          list: [
            "Angebotslogik: Bundle gegen Einzelprodukt",
            "Versandkostenschwelle und ihre Kommunikation",
            "Reihenfolge der Inhalte auf der Produktseite",
            "Art der Beweisführung: Bewertungen gegen Fotos von Kunden",
            "Kaufen-Button mit Kostenübersicht gegen nackten Button",
          ],
        },
        {
          h: "Der Aufbau, der Ergebnisse belastbar macht",
          p: [
            "Eine Hypothese in einem Satz: Wenn wir X ändern, steigt Y, weil Z. Eine Erfolgskennzahl, festgelegt vor dem Start, in der Regel Umsatz pro Session, nicht Klickrate.",
            "Dazu ein festes Enddatum, gleiche Traffic-Quellen in beiden Varianten und keine parallelen Änderungen am Shop. Sonst messen Sie das Wetter.",
          ],
        },
        {
          h: "Die vier Fehler, die Ergebnisse wertlos machen",
          p: [
            "Zu früh abbrechen, mehrere Dinge gleichzeitig ändern, nach dem Test nach Segmenten suchen, in denen die Variante doch gewonnen hat, und Micro-Conversions als Erfolg verkaufen. Jeder davon erzeugt Zahlen, die überzeugend aussehen und in die falsche Richtung führen.",
          ],
        },
        {
          h: "Die Alternative bei wenig Traffic",
          p: [
            "Bei kleineren Shops arbeite ich mit belegten Mustern, sauberen Vorher-Nachher-Zeiträumen und qualitativen Signalen aus Session-Recordings und Kundenbefragung. Das ist weniger elegant als ein Testergebnis, aber deutlich besser als eine Meinung.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-conversion-rate-benchmark", label: "Benchmark-Werte" },
        { to: "/methode", label: "Meine Methode" },
        { to: "/kontakt", label: "Store prüfen lassen" },
      ]}
    />
  );
}
